import React from 'react';
import HeroSection from '../components/HeroSection';
import Picture from '../components/Picture';
import SEO from '../components/SEO';

const HERO_IMG = '/images/heritage-thread-clay.jpg';
const ARTHUR_IMG = '/images/arthur.jpg';
const QUEEN_IMG = '/images/queen.jpg';
const HERITAGE_IMG = '/images/heritage-2022.jpg';

const galleryImages = [
  '/images/heritage-gallery-1.jpg',
  '/images/heritage-gallery-2.jpg',
  '/images/heritage-gallery-3.jpg',
  '/images/heritage-gallery-4.jpg',
  '/images/heritage-gallery-5.jpg',
  '/images/heritage-gallery-6.jpg',
];

export default function NewsPage() {
  return (
    <div data-testid="news-page">
      <SEO
        title="News"
        description="Latest news from Glossop & District Round Table 916. Updates on Bonfire Night, Heritage Weekend, community fundraising and local events in Glossop, Derbyshire."
        path="/news"
        keywords={['Glossop Round Table news', 'Glossop community news', 'Glossop bonfire news', 'Glossop heritage news']}
      />
      <HeroSection
        image={HERO_IMG}
        title="News"
        subtitle="Latest updates from Glossop & District Round Table 916"
        fullHeight={false}
        alt="Glossop Round Table volunteers in their orange Heritage Weekend t-shirts and hi-vis supporting a local independent shop, Thread & Clay Art School"
        captionTag="Heritage Weekend 2026"
      />

      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">

          {/* Glossopdale Community Champions Awards 2026 */}
          <article data-testid="news-gcc-2026" className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A]">
              Glossopdale Community Champions Awards 2026 &mdash; Judges&rsquo; Choice Winners!
            </h2>

            {/* Lead photo — the group holding the Judges' Choice trophy */}
            <Picture
              src="/images/awards-2026-3.jpg"
              alt="Glossop & District Round Table members on stage at the Glossopdale Community Champions Awards 2026, holding the WINNER certificate for the Adam Corbally Judges' Choice Award in front of giant light-up GCC letters"
              className="w-full h-auto object-cover"
            />

            <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
              <p>
                What a fantastic evening at the Glossopdale Community Champions Awards! We were incredibly proud to represent Glossop &amp; District Round Table and to be recognised alongside so many brilliant organisations and volunteers making a difference across our local community.
              </p>
              <p>
                We were delighted to receive recognition in the <strong>Outstanding Group of the Year</strong> category, but the biggest surprise of the evening was being presented with the <strong>Adam Corbally Judges&rsquo; Choice Award!</strong>
              </p>
              <p>
                It means a huge amount to have the work of our Round Table recognised. Everything we do &mdash; from organising events and raising money to supporting local causes &mdash; is only possible thanks to the hard work of our members and the fantastic support we receive from people across Glossopdale.
              </p>
              <p>
                A massive thank you to the organisers, judges and everyone involved in such a brilliant evening, and congratulations to all the other nominees and award winners. We&rsquo;re proud to be part of such an amazing community and we&rsquo;re looking forward to doing even more in the year ahead!
              </p>
            </div>

            {/* Supporting gallery */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Picture
                src="/images/awards-2026-2.jpg"
                alt="Round Table members on stage holding the Highly Commended certificate for Outstanding Group of the Year at the Glossopdale Community Champions Awards 2026"
                className="w-full h-56 object-cover"
              />
              <Picture
                src="/images/awards-2026-1.jpg"
                alt="Glass Judge's Choice Award trophy presented to Glossop & District Round Table at the Glossopdale Community Champions Awards 2026"
                className="w-full h-56 object-cover"
              />
              <Picture
                src="/images/awards-2026-4.jpg"
                alt="Glossop & District Round Table members pictured with a Glossopdale Community Champions sponsor backdrop at the Awards 2026"
                className="w-full h-56 object-cover"
              />
            </div>

            <p className="text-xs text-[#71717A] font-semibold uppercase tracking-wide pt-2">
              #Glossop &middot; #Glossopdale &middot; #GlossopRoundTable &middot; #RoundTable &middot; #Community &middot; #CommunityAwards &middot; #GlossopdaleCommunity &middot; #MakingADifference &middot; #DoMore
            </p>
          </article>

          <hr className="border-zinc-200" />

          {/* Bonfire 2026 */}
          <article data-testid="news-bonfire-2026" className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A]">
              Glossop Bonfire and Fireworks Spectacular – Saturday 7th November 2026
            </h2>
            <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
              <p>
                We at Glossop and District Round Table understand that not everyone likes fireworks and they can be distressing to animals, children and those with conditions like PTSD. We have pets and children who don't like them too. Because of that, we want to make sure everyone within earshot of Manor Park knows exactly when our displays are so they can take any precautions they need to.
              </p>
              <p>
                On Saturday 7th of November 2026 our Children's fireworks display will start at 1745 and last approximately 10 minutes. Our main display will start at 1915 and last approximately 20 minutes. Although we aim to set the fireworks off at these times, they may vary slightly due to weather conditions and other circumstances outside of our control. Whilst our Children's display has been toned down, both are a full fat fireworks displays so they are guaranteed to have some very loud bangs, pops and whistles. Please share this information with anyone you think needs it.
              </p>
              <p>
                We are not here to debate the current rules on fireworks and at the moment we are not looking to use low noise fireworks though, we do revisit this discussion every year during our planning.
              </p>
              <p>
                What we do want to encourage, is a safe place for people who want to enjoy a fireworks display with all the bangs, pops and whistles included. We encourage people to attend our displays rather than setting off their own. Hopefully this provides a great night for Glossop and District Round Table to raise money for charities, a great night for the community all while reducing the amount of fireworks being set off without warning.
              </p>
              <p>Below we have included some links to helpful websites:</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: 'RSPCA', url: 'https://www.rspca.org.uk/adviceandwelfare/pets/general/fireworks' },
                  { label: 'Dogs Trust', url: 'https://www.dogstrust.org.uk/dog-advice/life-with-your-dog/seasonal/fireworks' },
                  { label: "Cat's Protection", url: 'https://www.cats.org.uk/help-and-advice/home-and-environment/fireworks' },
                  { label: 'BBC – Fireworks for Children', url: 'https://www.bbc.co.uk/tiny-happy-people/bonfire-night-kids-scared-fireworks/z7pyydm' },
                  { label: 'Help with PTSD', url: 'https://patient.info/news-and-features/managing-ptsd-during-fireworks-season' },
                ].map((link) => (
                  <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#F0A500] hover:text-[#D99500] transition-colors">
                    {link.label} &rarr;
                  </a>
                ))}
              </div>
            </div>
          </article>

          <hr className="border-zinc-200" />

          {/* Bonfire Tickets */}
          <article data-testid="news-bonfire-tickets" className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A]">
              Glossop Bonfire and Fireworks Spectacular – Tickets
            </h2>
            <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
              <p>
                <strong>Glossop Bonfire and Fireworks Spectacular</strong> in Manor Park, Glossop on Saturday 8th November 2025. We can't wait to welcome you to this years event. Last year was a very successful evening. We raised over &pound;18,000 for local good causes and charities like Hadfield Coming Together Group, People Of Whitfield (POW), Crossroads Derbyshire, Glossopdale Foodbank and many more.
              </p>
              <p>
                Advance ticket pricing is as follows, Family tickets are &pound;25.00 and include two adults and two concessions. Adult tickets aged 16-65 are &pound;10.00 and for Concession tickets aged 3-15, Students, anyone in receipt of DLA or PIP (please bring proof with you as you maybe asked on the gate) and Adults aged 66+ are &pound;5.00. Under 3s can enter for free.
              </p>
              <p>
                On the gate ticket pricing is as follows, Family tickets are &pound;33.00 and include two adults and two concessions. Adult tickets aged 16-65 are &pound;13.00 and for Concession tickets aged 3-15, Students, anyone in receipt of DLA or PIP (please bring proof with you as you maybe asked on the gate) and Adults aged 66+ are &pound;7.00. Under 3s can enter for free.
              </p>
            </div>
          </article>

          <hr className="border-zinc-200" />

          {/* Arthur */}
          <article data-testid="news-arthur" className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A]">
              Arthur Ibbotson 1933 – 2023
            </h2>
            <Picture src={ARTHUR_IMG} alt="Arthur Ibbotson" className="w-full max-w-sm h-auto object-cover" />
            <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
              <p>Some incredibly sad news to announce. Our first Chairman and founding member, Dr Arthur Ibbotson has passed away.</p>
              <p>Arthur founded Glossop and District Round Table around 1966. He was instrumental in inaugurating the Glossop Bonfire and has helped out at all Bonfires since then. Arthur was always on hand to give advice and guidance to members.</p>
              <p>He was our President for the Table year 2023-2024 and it is a fitting tribute that Arthur's name will be on the Presidents chain again.</p>
              <p>He was a keen walker and would take Tablers half his age up peaks, regularly going on 20 mile hikes.</p>
              <p>We would like to send our thoughts to Arthur's family and will be looking to commemorate Arthur as the founder of Glossop Round Table in the near future.</p>
              <p>Glossop & District Round Table have raised around &pound;400,000 over the years for local charities and good causes. This would not have been possible without Arthur's foresight and guiding hand.</p>
              <p className="font-semibold text-[#0A0A0A]">Rest in peace Arthur</p>
            </div>
          </article>

          <hr className="border-zinc-200" />

          {/* Queen */}
          <article data-testid="news-queen" className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A]">
              Queen Elizabeth II – 8th September 2022
            </h2>
            <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
              <p>Terribly sad news today to hear that Queen Elizabeth II has passed away, she was a truly great Monarch. The Queen and Prince Philip have always supported the charitable work that Round Table Great Britain and Ireland do in the Community. Thank you Ma'am. RTBI.</p>
              <Picture src={QUEEN_IMG} alt="Thank You Ma'am" className="w-full max-w-md h-auto object-cover" />
            </div>
          </article>

          <hr className="border-zinc-200" />

          {/* Heritage 2022 */}
          <article data-testid="news-heritage-2022" className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A]">
              Glossop Victorian and Heritage – 5th June 2022
            </h2>
            <Picture src={HERITAGE_IMG} alt="Glossop Victorian & Heritage Weekend 2022" className="w-full h-auto object-cover" />
            <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
              <p>The dust has settled and it has to be said, we had an amazingly successful weekend at the Glossop Victorian and Heritage event with thousands raised for local charities. Further details of the weekend can be found <a href="https://glossopheritageweekend.org/" target="_blank" rel="noopener noreferrer" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">here</a>.</p>
              <p>Thank you to everyone who made it possible and to all those who came and enjoyed the festivities. So many photos and videos shared, lots of great memories for years to come!</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {galleryImages.map((img) => (
                <Picture key={img} src={img} alt="Heritage Weekend" className="w-full h-48 object-cover" />
              ))}
            </div>
          </article>

        </div>
      </section>
    </div>
  );
}
